/**
 * #/connect/<request>: Claude (or another MCP client) asks to use Whiteboard Studio as you. Shown
 * after you sign in; your answer sends the browser back to the app.
 */

import { Bot, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { api, type User } from "./api";

export function Connect({ rid, user }: { rid: string; user: User }) {
  const [req, setReq] = useState<{ client: string; redirect: string; access: string } | null>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(false);
  useEffect(() => {
    api.oauthRequest(rid).then(setReq).catch((e) => setErr(e.message));
  }, [rid]);
  const answer = async (allow: boolean) => {
    setBusy(true);
    try {
      const r = await api.oauthAnswer(rid, allow, edit);
      location.href = r.redirect;
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  };
  return (
    <div className="login connect">
      <div className="login-box">
        <h1><Bot size={22} /> Connect to Whiteboard Studio</h1>
        {err && <p className="bad">{err}</p>}
        {!req && !err && <p className="muted"><Loader2 size={14} className="spin" /> Loading…</p>}
        {req && (
          <>
            <p><strong>{req.client}</strong> wants to use Whiteboard Studio as <strong>{user.name || user.email}</strong>.</p>
            <ul className="small">
              <li>It can see the projects you're a member of, their scenes, layers and captions, and the shared library.</li>
              <li>It can render still frames to look at your videos.</li>
              <li>{edit ? <><strong>Editing allowed:</strong> it can change text, timing, transitions and layout, add or delete layers, regenerate captions and start renders. Every change is saved as a version you can undo from History.</> : <><strong>Read-only:</strong> it can't change, delete or share anything.</>}</li>
              <li>It can never delete projects, share them or change people's access.</li>
            </ul>
            <label className="check connect-edit">
              <input type="checkbox" checked={edit} onChange={(e) => setEdit(e.target.checked)} /> Allow it to edit my projects
            </label>
            <p className="muted small">
              You'll go back to <code>{req.redirect}</code>. You can disconnect it any time under Account → Connected apps.
            </p>
            <div className="row-buttons">
              <button className="primary" disabled={busy} onClick={() => answer(true)}>Allow</button>
              <button disabled={busy} onClick={() => answer(false)}>Deny</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
